import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
// eslint-disable-next-line no-restricted-imports
import type { TemplateCategory as TemplateCategoryType } from '@trello/graphql/generated';
import { NetworkError } from '@trello/graphql-error-handling';
import { intl } from '@trello/i18n';
import { BoardIcon } from '@trello/nachos/icons/board';
import { TemplateBoardIcon } from '@trello/nachos/icons/template-board';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';
import { convertToPIIString } from '@trello/privacy';
import type { TemplatesTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { Footer } from 'app/src/components/Footer';
import { Navigation } from 'app/src/components/Navigation';
import { SearchWrapper } from './SearchWrapper';
import { useTemplateCategoriesQuery } from './TemplateCategoriesQuery.generated';
import { TemplateCategory } from './TemplateCategory';
import { TemplateGalleryContentError } from './TemplateGalleryContentError';
import { useTemplateGalleryQuery } from './TemplateGalleryQuery.generated';
import { TemplatesFeatured } from './TemplatesFeatured';
import { TemplateStory } from './TemplateStory';
import type {
  CategoryData,
  Template,
  TemplateLanguage,
  Templates,
} from './types';

import styles from './TemplatesGalleryContent.less';

const loadingSpinner = (
  <div className={classNames(styles.spinnerContainer, styles.spinner)}>
    <Spinner />
  </div>
);

export const normalizeTemplates = (templates: Templates): Template[] => {
  return templates.map((t) => ({
    id: t.id,
    idOrganization: t.idOrganization ?? '',
    name: t.name ?? '',
    shortLink: t.shortLink ?? '',
    prefs: {
      backgroundColor: t.prefs?.backgroundColor ?? undefined,
      backgroundImage: t.prefs?.backgroundImage ?? undefined,
    },
    templateGallery: {
      avatarUrl: t.templateGallery?.avatarUrl ?? '',
      avatarShape: t.templateGallery?.avatarShape ?? '',
      byline: t.templateGallery?.byline ?? '',
      category: t.templateGallery?.category ?? '',
      precedence: t.templateGallery?.precedence ?? 0,
      blurb: t.templateGallery?.blurb ?? '',
      featured: t.templateGallery?.featured ?? false,
      stats: {
        viewCount: t.templateGallery?.stats?.viewCount ?? 0,
        copyCount: t.templateGallery?.stats?.copyCount ?? 0,
      },
    },
  }));
};

interface TemplatesGalleryContentProps {
  templateLanguage: TemplateLanguage;
  languages: TemplateLanguage[];
  category?: string;
  isInAppGallery: boolean;
  shortLink?: string;
}

export const TemplatesGalleryContent: FunctionComponent<
  TemplatesGalleryContentProps
> = ({ templateLanguage, languages, category, isInAppGallery, shortLink }) => {
  const {
    error: categoriesError,
    loading: categoriesLoading,
    data: categoriesData,
  } = useTemplateCategoriesQuery();

  const {
    data: templatesGalleryData,
    error: templatesGalleryError,
    loading: templatesGalleryLoading,
  } = useTemplateGalleryQuery({
    variables: {
      locale: convertToPIIString(templateLanguage.language),
    },
    waitOn: ['MemberHeader'],
  });

  const [navigationExpanded, setNavigationExpanded] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const analyticsFn = useCallback((key: any) => {
    Analytics.sendClickedLinkEvent({
      linkName: 'templateCategoryLink',
      source: 'templateGalleryScreen',
      attributes: {
        category: key,
      },
    });
  }, []);

  /**
   * sendErrorEvent is NOT being called for these specific errors because the errors are thrown by `window.fetch`, but not
   * for non-2XX responses. They're only thrown when fetch itself breaks, which could be caused by:
   *
   *   - DNS issues
   *   - Bad HTTP headers
   *   - Malformed URL
   *   - The request was aborted by the browser (user navigates to another page before the request completes)
   *   - The request was blocked by an extension (i.e., an ad-blocker)
   *
   * We're also excluding network errors since we should already have called sendNetworkErrorEvent in the resolver
   *
   * Source: https://stackoverflow.com/questions/45383874/fetch-api-yields-typeerror-failed-to-fetch/47423883#47423883
   */
  useEffect(() => {
    if (
      categoriesError &&
      !(
        categoriesError.message.includes('TypeError: Failed to fetch') ||
        categoriesError instanceof NetworkError
      )
    ) {
      sendErrorEvent(categoriesError, {
        tags: {
          ownershipArea: 'trello-web-eng',
        },
        extraData: {
          networkError: categoriesError.networkError
            ? categoriesError.networkError.message
            : false,
          extraInfo: categoriesError.extraInfo,
          graphQLErrors: categoriesError.graphQLErrors.toString(),
          stack: categoriesError.stack ? categoriesError.stack : false,
          name: categoriesError.name,
        },
      });
    }
  }, [categoriesError]);

  useEffect(() => {
    if (
      templatesGalleryError &&
      !(
        templatesGalleryError.message.includes('TypeError: Failed to fetch') ||
        templatesGalleryError instanceof NetworkError
      )
    ) {
      sendErrorEvent(templatesGalleryError, {
        tags: {
          ownershipArea: 'trello-web-eng',
        },
        extraData: {
          networkError: templatesGalleryError.networkError
            ? templatesGalleryError.networkError.message
            : false,
          extraInfo: templatesGalleryError.extraInfo,
          graphQLErrors: templatesGalleryError.graphQLErrors.toString(),
          stack: templatesGalleryError.stack
            ? templatesGalleryError.stack
            : false,
          name: templatesGalleryError.name,
        },
      });
    }
  }, [templatesGalleryError]);

  if (categoriesError || templatesGalleryError) {
    return <TemplateGalleryContentError />;
  }

  let categories: TemplateCategoryType[] = [];
  let categoriesNav: CategoryData[] = [];

  if (categoriesData) {
    categories = categoriesData.templateCategories;
    categoriesNav = categories.map((c) => ({
      key: c.key,
      url: c.key,
      title: intl.formatMessage({
        id: `templates.templates.${c.key}`,
        defaultMessage: 'Category',
        description: 'A title for a category of Trello templates',
      }),
      classes: { navigationLink: '' },
    }));
  }

  let normalizedTemplates: Template[] = [];
  let username: PIIString | undefined;
  let templates: Templates = [];
  if (templatesGalleryData) {
    templates = templatesGalleryData.templateGallery;
    username = templatesGalleryData?.member?.username;
    normalizedTemplates = normalizeTemplates(templates);
  }

  let section;
  if (category) {
    section = 'category';
  }

  let navigation;
  if (!isInAppGallery) {
    const navigationLink = styles.navigationLink;
    navigation = (
      <Navigation
        categories={categoriesNav}
        classes={{ navigationLink }}
        navigationExpanded={navigationExpanded}
        setNavigationExpanded={setNavigationExpanded}
        urlSlug="templates"
        analyticsFn={analyticsFn}
        boardIcon={<BoardIcon />}
        viewAllIcon={<TemplateBoardIcon />}
        SearchComponent={<SearchWrapper templates={normalizedTemplates} />}
        section={section}
        subsection={category}
      ></Navigation>
    );
  }

  let gallery;
  if (categoriesLoading || templatesGalleryLoading) {
    gallery = loadingSpinner;
  } else {
    if (category && shortLink) {
      gallery = (
        <TemplateStory
          category={category}
          shortLink={shortLink}
          templates={normalizedTemplates}
          isInAppGallery={isInAppGallery}
          username={username}
        />
      );
    } else if (category) {
      gallery = (
        <TemplateCategory
          category={category}
          templates={normalizedTemplates}
          isInAppGallery={isInAppGallery}
        />
      );
    } else {
      gallery = (
        <TemplatesFeatured
          categories={categories}
          templates={normalizedTemplates}
          isInAppGallery={isInAppGallery}
        />
      );
    }
  }

  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.containerLoggedOut]: !isInAppGallery,
        })}
        data-testid={getTestId<TemplatesTestIds>('templates-container')}
      >
        {navigation}
        <div className={styles.galleryContainer}>{gallery}</div>
      </div>
      {!isInAppGallery && (
        <>
          <hr />
          <Footer
            languages={languages}
            isLoggedOut={true}
            templateLanguage={templateLanguage}
          />
        </>
      )}
    </>
  );
};
